// biome-ignore-all lint/suspicious/noExplicitAny: better typings are welcome
export const ConsoleService = {
  isStencilWarning(...args: any[]): boolean {
    return args[0]?.startsWith('STENCIL');
  },

  /**
   * A whitelist of errors and warnings that should not be printed to the
   * console if the verbosity level is set to `no-whitelist`. Only include
   * errors and warnings that are harmless or outside of our control. Each
   * predicate in a list takes the same arguments as the original console
   * method.
   */
  get whitelist(): Record<string, Array<(...args: any[]) => boolean>> {
    return {
      warn: [ConsoleService.isStencilWarning.bind(ConsoleService)],
      error: [],
    };
  },

  /**
   * Control the verbosity of the console output.
   */
  setVerbosity(verbosity: 'quiet' | 'no-whitelist' | 'default'): void {
    if (verbosity === 'default') {
      return;
    }

    // Suppress all warnings and errors
    if (verbosity === 'quiet') {
      ['warn', 'error'].forEach((method) => {
        ConsoleService.adjustConsoleMethod(method as 'warn' | 'error', () => true);
      });
    }

    // Do not print warnings and errors that are whitelisted
    if (verbosity === 'no-whitelist') {
      Object.entries(ConsoleService.whitelist).forEach(([method, predicates]) => {
        ConsoleService.adjustConsoleMethod(method as 'warn' | 'error', (...args) =>
          (predicates as Array<(...args: any[]) => boolean>).some((predicate) => predicate(...args)),
        );
      });
    }
  },

  /**
   * Adjust a console `method` such that messages for which the `predicate`
   * returns `true` are not printed. The `predicate` takes the same arguments as
   * the original console method.
   */
  adjustConsoleMethod(method: 'warn' | 'error', predicate: (...args: any[]) => boolean): void {
    const originalConsoleMethod = console[method];

    console[method] = (...args: any[]) => {
      if (predicate(...args)) {
        return;
      }

      originalConsoleMethod(...args);
    };
  },
};
